import { DEG, TAU, rk4, formatNumber } from '../math.js';

function energyOf(state, params) {
  const { radius: R, mass: m, gravity: g } = params;
  const speed = R * state.omega;
  const kinetic = 0.5 * m * speed * speed;
  const potential = m * g * R * (1 - Math.cos(state.theta));
  return { kinetic, potential, total: kinetic + potential };
}

export const circular = {
  id: 'circular',
  order: 4,
  meta: {
    title: '圆周运动',
    shortTitle: '圆周',
    eyebrow: 'CENTRIPETAL DYNAMICS',
    tagline: '让速度与重力共同画出圆',
    description:
      '让小球沿竖直圆轨道运动，分解切向与法向加速度，观察向心力、杆作用力和临界速度，理解圆周运动的动力学条件。',
    accent: '#f472b6',
    accentRgb: '244, 114, 182',
    grade: '高中物理 · 圆周运动',
    duration: '建议 15–20 分钟',
    objectives: [
      '理解向心加速度与向心力的方向、大小和来源，会在最低点、最高点列径向动力学方程。',
      '利用机械能守恒求小球在竖直圆轨道上不同位置的速度。',
      '区分轻杆模型与轻绳模型的最高点临界条件。',
      '观察变速圆周运动中切向加速度与法向加速度的共同作用。',
    ],
    apparatus: ['竖直圆轨道（或轻杆模型）', '小球', '速度传感器', '力传感器', '米尺', 'PhysLab 仿真终端'],
    theory: [
      '小球在竖直圆轨道内侧运动，取最低点为势能零点，机械能 $E=\\tfrac12mv^2+mgR(1-\\cos\\theta)$。无阻尼时机械能守恒。',
      '沿半径方向的牛顿第二定律：$N+mg\\cos\\theta=\\dfrac{mv^2}{R}$。因此轨道/杆对球的作用力 $N=\\dfrac{mv^2}{R}+mg\\cos\\theta$。',
      '最低点 $\\theta=0$：$N=mg+\\dfrac{mv_0^2}{R}$；最高点 $\\theta=\\pi$：$N=\\dfrac{mv_{\\mathrm{top}}^2}{R}-mg$。轻杆的 $N$ 可以为负，表示杆对球施加向外的支持力。',
      '由能量守恒，$v_{\\mathrm{top}}^2=v_0^2-4gR$。轻杆模型只需 $v_0>2\\sqrt{gR}$ 即可到达最高点；轻绳模型还要求最高点 $N\\ge0$，即 $v_0\\ge\\sqrt{5gR}$。',
      '切向加速度 $a_t=-g\\sin\\theta$ 改变速度大小，法向加速度 $a_n=v^2/R$ 改变速度方向。',
    ],
    procedure: [
      '测量圆轨道半径 $R$ 和小球质量 $m$。',
      '让小球在最低点获得设定的初速度，确认小球沿竖直圆轨道运动。',
      '用传感器记录小球通过最低点与最高点的速度，计算向心加速度。',
      '在最低点和最高点分别列径向动力学方程，求轨道对球的作用力。',
      '改变初速度，观察“完整圆周”“往复摆动”两种运动状态。',
      '用能量守恒预测最高点速度，并与仿真值比较。',
    ],
    safety: [
      '确保轨道连接牢固，防止小球高速运动时脱轨。',
      '初速度不宜过大，避免小球越出轨道或撞击传感器。',
      '使用轻杆模型时注意杆端固定，防止旋转时甩出。',
    ],
    formulas: [
      { label: '向心加速度', latex: 'a_n=\\dfrac{v^2}{R}=R\\omega^2', note: '指向圆心' },
      { label: '径向动力学', latex: 'N+mg\\cos\\theta=\\dfrac{mv^2}{R}', note: 'θ 从最低点量起' },
      { label: '最高点速度', latex: 'v_{\\mathrm{top}}=\\sqrt{v_0^2-4gR}', note: '机械能守恒' },
      { label: '轻杆临界速度', latex: 'v_{0,\\min}=2\\sqrt{gR}', note: '轻绳为 √(5gR)' },
    ],
    recordTable: {
      title: '竖直圆周运动速度—作用力记录表',
      columns: ['次数', '半径 R / m', '质量 m / kg', '最低点速度 v₀ / (m·s⁻¹)', '最高点速度 v / (m·s⁻¹)', '最低点 N / N', '最高点 N / N'],
      rows: 5,
    },
  },
  schema: [
    {
      key: 'radius',
      label: '轨道半径',
      symbol: 'R',
      unit: 'm',
      min: 0.3,
      max: 3,
      step: 0.05,
      default: 1.2,
      precision: 2,
      group: '轨道几何',
      apply: 'reset',
      description: '竖直圆轨道的半径。半径越大，通过最高点所需的最低速度越大。',
    },
    {
      key: 'mass',
      label: '小球质量',
      symbol: 'm',
      unit: 'kg',
      min: 0.1,
      max: 5,
      step: 0.1,
      default: 0.5,
      precision: 1,
      group: '小球参数',
      apply: 'live',
      description: '质量影响作用力与能量，但不改变无阻尼时的速度分布。',
    },
    {
      key: 'gravity',
      label: '重力加速度',
      symbol: 'g',
      unit: 'm/s²',
      min: 1.6,
      max: 20,
      step: 0.1,
      default: 9.8,
      precision: 1,
      group: '环境参数',
      apply: 'live',
      description: '改变重力环境会改变临界速度与最高点作用力。',
    },
    {
      key: 'initialSpeed',
      label: '最低点初速度',
      symbol: 'v₀',
      unit: 'm/s',
      min: 1,
      max: 15,
      step: 0.1,
      default: 8.5,
      precision: 1,
      group: '初始条件',
      apply: 'reset',
      description: '小球在最低点获得的初速度。大于 √(5gR) 时可完整绕行（轻绳模型）。',
    },
    {
      key: 'damping',
      label: '阻尼系数',
      symbol: 'b',
      unit: '1/s',
      min: 0,
      max: 0.3,
      step: 0.01,
      default: 0,
      precision: 2,
      group: '环境参数',
      apply: 'live',
      description: '等效空气阻力。为 0 时机械能守恒；增大后小球可能无法维持完整圆周。',
    },
  ],
  createState(params) {
    return {
      t: 0,
      theta: 0,
      omega: params.initialSpeed / params.radius,
    };
  },
  advance(state, params, dt) {
    const { radius: R, gravity: g, damping: b } = params;
    const derivative = ([theta, omega]) => [omega, -(g / R) * Math.sin(theta) - b * omega];
    const [theta, omega] = rk4([state.theta, state.omega], dt, derivative);
    return { t: state.t + dt, theta, omega };
  },
  measure(state, params) {
    const { radius: R, mass: m, gravity: g, initialSpeed, damping } = params;
    const energy = energyOf(state, params);
    const speed = Math.abs(state.omega) * R;
    const centripetalAcceleration = speed * speed / R;
    const centripetalForce = m * centripetalAcceleration;
    const normalForce = m * (R * state.omega * state.omega + g * Math.cos(state.theta));
    const height = R * (1 - Math.cos(state.theta));
    const topEnergy = energy.total - 2 * m * g * R;
    const currentTopSpeed = topEnergy > 0 ? Math.sqrt((2 * topEnergy) / m) : 0;
    const initialTopSpeedSq = initialSpeed * initialSpeed - 4 * g * R;
    const predictedTopSpeed = initialTopSpeedSq > 0 ? Math.sqrt(initialTopSpeedSq) : 0;
    const criticalRod = Math.sqrt(4 * g * R);
    const criticalString = Math.sqrt(5 * g * R);
    const canLoop = topEnergy > 1e-9;
    const laps = Math.max(0, Math.floor(state.theta / TAU));
    const status = canLoop
      ? { tone: 'ok', label: '完整圆周运动', detail: '小球具备通过最高点的能量' }
      : { tone: 'warning', label: '往复摆动', detail: '能量不足以通过最高点，小球在轨道下半部往复运动' };

    return {
      time: state.t,
      energy,
      momentum: null,
      status,
      readouts: [
        { key: 'speed', label: '线速度', value: speed, unit: 'm/s', precision: 2 },
        { key: 'an', label: '向心加速度', value: centripetalAcceleration, unit: 'm/s²', precision: 2 },
        { key: 'fc', label: '向心力', value: centripetalForce, unit: 'N', precision: 2 },
        { key: 'normal', label: '杆作用力 N', value: normalForce, unit: 'N', precision: 2 },
        {
          key: 'topSpeed',
          label: '最高点预测速度',
          value: canLoop ? currentTopSpeed : 0,
          unit: 'm/s',
          precision: 2,
          hint: canLoop ? '由当前机械能计算' : '当前能量不足以到达最高点',
        },
        { key: 'critical', label: '轻杆临界初速度', value: criticalRod, unit: 'm/s', precision: 2 },
        { key: 'laps', label: '完整圈数', value: laps, unit: '圈', precision: 0 },
      ],
      extras: {
        speed,
        centripetalAcceleration,
        centripetalForce,
        normalForce,
        height,
        thetaDeg: (state.theta / DEG) % 360,
        topSpeed: currentTopSpeed,
        predictedTopSpeed,
        criticalRod,
        criticalString,
        canLoop,
        laps,
        damping,
      },
    };
  },
  reference(state, params) {
    return { energy: energyOf(state, params).total, momentum: null };
  },
  conservation(params) {
    return { energy: params.damping > 0 ? 'dissipative' : 'conserved', momentum: null };
  },
  isFinished() {
    return false;
  },
  describe(params) {
    const critical = Math.sqrt(4 * params.gravity * params.radius);
    return `轻杆通过最高点的临界初速度 ${formatNumber(critical, 2)} m/s；轻绳需 ${formatNumber(Math.sqrt(5 * params.gravity * params.radius), 2)} m/s`;
  },
};
